import { useCallback, useId, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import RadioButton from 'LuxKit/Radio/RadioButton'
import useFlightSortOptions from 'hooks/Flights/useFlightSortOptions'
import type { FlightSortOptions } from 'constants/flight'
import { getOtherSortOptions } from 'lib/flights/flightUtils'

const FlightSortOptionsList = styled.div`
  min-width: ${rem(232)};
  padding: ${rem(8)} 0;
`

interface FlightSortOption {
  label: string
  value: FlightSortOptions
}

interface ChatFlightSortDropdownOptionProps {
  name: string
  option: FlightSortOption
  selected: boolean
  onSelect: (option: FlightSortOption) => void
}

function ChatFlightSortDropdownOption(props: ChatFlightSortDropdownOptionProps) {
  const { name, option, selected, onSelect } = props

  const handleSelect = useCallback(() => {
    onSelect(option)
  }, [onSelect, option])

  return (
    <ClickableListItem
      title={option.label}
      size="medium"
      selected={selected}
      startIcon={<RadioButton name={name} value={option.value} checked={selected} />}
      onClick={handleSelect}
    />
  )
}

interface Props {
  sort: FlightSortOptions
  originAirportCode?: string
  destinationAirportCode?: string
  onChange: (sort: FlightSortOptions) => void
}

function ChatFlightSortDropdown(props: Props) {
  const {
    sort,
    originAirportCode = '',
    destinationAirportCode = '',
    onChange,
  } = props
  const intl = useIntl()
  const sortOptions = useFlightSortOptions()
  const radioGroupName = useId()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  const otherSortOptions = useMemo<Array<FlightSortOption>>(() => {
    return getOtherSortOptions(originAirportCode, destinationAirportCode)
  }, [destinationAirportCode, originAirportCode])

  const allSortOptions = useMemo<Array<FlightSortOption>>(() => [
    ...sortOptions,
    ...otherSortOptions,
  ], [otherSortOptions, sortOptions])

  const selectedSortLabel = useMemo(() => {
    return allSortOptions.find(option => option.value === sort)?.label ?? sortOptions[0]?.label
  }, [allSortOptions, sort, sortOptions])

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev)
  }, [])

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleSelect = useCallback((option: FlightSortOption) => {
    onChange(option.value)
    closeDropdown()
  }, [closeDropdown, onChange])

  return (
    <>
      <FilterChip
        size="small"
        selected
        onClick={toggleDropdown}
        ref={dropdownTriggerRef}
        endIcon={<DropdownChevron open={isDropdownOpen} />}
        aria-label={intl.formatMessage({
          id: 'uRbyQofjZRIuIUFFnkV2',
          defaultMessage: 'Sort flights',
          description: 'Accessible label for flight sorting dropdown in trip planner chat',
        })}
      >
        {selectedSortLabel}
      </FilterChip>
      <DropdownList
        size="S"
        open={isDropdownOpen}
        onClose={closeDropdown}
        triggerRef={dropdownTriggerRef}
        anchorRef={dropdownTriggerRef}
        portaled
      >
        <FlightSortOptionsList>
          {allSortOptions.map(option => (
            <ChatFlightSortDropdownOption
              key={option.value}
              name={radioGroupName}
              option={option}
              selected={sort === option.value}
              onSelect={handleSelect}
            />
          ))}
        </FlightSortOptionsList>
      </DropdownList>
    </>
  )
}

export default ChatFlightSortDropdown
